import DOMPurify from 'dompurify';

/** Non-breaking space, as an escape so the character stays visible in source. */
const NBSP = /&nbsp;|\u00A0/g;

/**
 * CPzone's rich-text editor separates EVERY word with `&nbsp;` rather than a
 * plain space — a real description arrives with ~190 non-breaking spaces and
 * zero ASCII ones. This is the norm, not an edge case: 412 of production's 427
 * published listings have descriptions written this way. Because a non-breaking space is not a line-break
 * opportunity, each paragraph becomes a single unbreakable 600+ character
 * "word" that refuses to wrap, overflows its column and slides underneath
 * the sticky contact rail on the property detail page.
 *
 * Collapsing them back to ordinary spaces restores normal wrapping. Runs of
 * consecutive nbsp (used for indentation) collapse the way real spaces would
 * in HTML, so nothing meaningful is lost. Applied after sanitizing so it only
 * ever touches DOMPurify's already-safe output — swapping a space for a space
 * cannot reintroduce markup.
 */
function collapseNonBreakingSpaces(html: string): string {
  return html.replace(NBSP, ' ');
}

/**
 * Sanitizes rich-text HTML from CPzone (property descriptions, etc.) before
 * it's ever injected via `dangerouslySetInnerHTML`. CPzone is a third-party
 * CRM, not user input at request time, but its content still isn't trusted
 * enough to inline unsanitized — this strips scripts/event handlers/etc.
 * while keeping basic formatting tags.
 */
export function sanitizeHtml(html: string): string {
  return collapseNonBreakingSpaces(
    DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'span', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    }),
  );
}

/** Strips all markup down to plain text — for contexts (JSON-LD, meta
 *  descriptions, card previews) that need a plain string, not markup. */
export function htmlToPlainText(html: string): string {
  return collapseNonBreakingSpaces(
    DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }),
  ).trim();
}
